import { useEffect, useMemo, useState } from 'react';
import { Eye, Pencil, Plus, X, Trash2 } from 'lucide-react';
import {
  createEmpresa,
  deleteEmpresa,
  getEmpresa,
  updateEmpresa,
} from '../services/empresaService';
import { createContato, getContato } from '../services/contatoService';
import { getUser, usuarioPossuiAssinatura } from '../services/usersService';
import { getSessionClientId, getSessionRole } from '../services/authSession';
import { getCidadesByUf, UF_OPTIONS } from '../services/cidadeService';
import ExpandableSearchFilter from '../components/ExpandableSearchFilter';
import RecordViewModal from '../components/RecordViewModal';
import SearchAutocompleteSelect from '../components/SearchAutocompleteSelect';
import { showSuccessToast, showErrorToast } from '../utils/notifications';
import { onlyDigits, formatCnpj, formatCpf, formatCep, formatPhone } from '../utils/formatters';
import { normalizeContato, normalizeContatoOption, normalizeEmpresa, asArray } from '../utils/normalizers';
import useSubmitLock from '../hooks/useSubmitLock';

/*const mockCidades = [{ id: 1, nome: 'Sao Paulo' }, { id: 2, nome: 'Campinas' }, { id: 3, nome: 'Santos' }];*/
/*const mockClientes = [{ id: 'cli-1', nome: 'Cliente A' }, { id: 'cli-2', nome: 'Cliente B' }, { id: 'cli-3', nome: 'Cliente C' }];*/
/*const mockUsuariosTecnicos = [{ id: 'usr-1', nome: 'Tecnico 1' }, { id: 'usr-2', nome: 'Tecnico 2' }, { id: 'usr-3', nome: 'Tecnico 3' }];*/
/*const mockUsuariosFinanceiros = [{ id: 'usr-4', nome: 'Financeiro 1' }, { id: 'usr-5', nome: 'Financeiro 2' }, { id: 'usr-6', nome: 'Financeiro 3' }];*/
/*const mockUsuariosAdministrativos = [{ id: 'usr-7', nome: 'Administrativo 1' }, { id: 'usr-8', nome: 'Administrativo 2' }, { id: 'usr-9', nome: 'Administrativo 3' }];*/

/*const INITIAL_CONTACTS = [
  ...mockUsuariosTecnicos.map((item) => ({ ...item, tipo: 'tecnico' })),
  ...mockUsuariosFinanceiros.map((item) => ({ ...item, tipo: 'financeiro' })),
  ...mockUsuariosAdministrativos.map((item) => ({ ...item, tipo: 'administrativo' })),
];*/

// AJUSTE BACKEND AQUI: fallback local de contatos.
// Quando o endpoint existir, substituir por GET /api/contato.

/*const mockEmpresasInicial = [
  { id_empresa: 1, id_cliente: 'cli-1', razao_social: 'Kretos Lab', numr_cnpj: '12345678000199', numr_cpf: '', endereco: 'Rua Central, 100', codg_cep: 12345678, id_cidade: 1, id_usuario_tecnico: 'usr-1', id_usuario_financeiro: 'usr-4', id_usuario_administrativo: 'usr-7', criado_em: '2026-03-01T12:00:00.000Z' },
  { id_empresa: 2, id_cliente: 'cli-2', razao_social: 'Concreteira Azul', numr_cnpj: '22345678000188', numr_cpf: '', endereco: 'Av. Industrial, 250', codg_cep: 87654321, id_cidade: 2, id_usuario_tecnico: 'usr-2', id_usuario_financeiro: 'usr-5', id_usuario_administrativo: 'usr-8', criado_em: '2026-03-10T12:00:00.000Z' },
]; */

const INITIAL_FORM_DATA = { idCliente: '', razaoSocial: '', numrCnpj: '', numrCpf: '', endereco: '', codgCep: '', idCidade: '', idUsuarioTecnico: '', idUsuarioFinanceiro: '', idUsuarioAdministrativo: '', idUsuarioAssinatura1: '', idUsuarioAssinatura2: '' };

// Identifica teclas de navegação/controle que devem passar mesmo nos campos com máscara.
const isControlKey = (event) => {
  return [
    'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter', 'Escape'
  ].includes(event.key);
};

// Resolve o nome de um item (contato/cidade) a partir do id; retorna '-' se não achar.
function findLabel(list, id) {
  const item = list.find((entry) => String(entry.id || entry.idContato || entry.id_contato || entry.idCidade) === String(id));
  return item ? item.nome || item.nomeCompleto || item.nomeCidade : '-';
}

// Tela de Gestão de Empresas: CRUD com documento (CNPJ/CPF), cidade e contatos (técnico/financeiro/administrativo); modal com aba de cadastro rápido de contato.
function EmpresaPage() {
  const role = getSessionRole();
  const canManage = role === 'admin';
  const canCreate = role === 'admin' || role === 'consultor' || !role;
  const sessionClientId = getSessionClientId();
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUf, setSelectedUf] = useState('MT');
  /*const [cidades, setCidades] = useState(mockCidades);*/
  /*const [cidadesTodas, setCidadesTodas] = useState(mockCidades);*/
  const [query, setQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeModalTab, setActiveModalTab] = useState('empresa');
  const [editingEmpresaId, setEditingEmpresaId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [contactFormErrors, setContactFormErrors] = useState({});
  const [contatos, setContatos] = useState([]);
  // AJUSTE BACKEND AQUI: guardar aqui a lista retornada da API para alimentar tabela e selects de contato.
  const [contactFormData, setContactFormData] = useState({
    nomeCompleto: '',
    numrCpf: '',
    numrTelefone: '',
    enderecoEmail: '',
  });
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [cidades, setCidades] = useState([]);
  const [viewEmpresa, setViewEmpresa] = useState(null);
  // Usuários do sistema; os que têm assinatura podem ser vinculados como assinantes dos relatórios.
  const [usuarios, setUsuarios] = useState([]);

  // Contato fica genérico; a função (técnico/financeiro/administrativo) é definida no select da empresa.
  const contatosDisponiveis = useMemo(
    () => contatos.map(normalizeContatoOption).filter((item) => item.id),
    [contatos]
  );

  const usuariosOptions = useMemo(
    () => asArray(usuarios)
      .map((u) => ({
        id: String(u.idUsuario || u.id || ''),
        nome: u.nomeCompleto || u.nome || u.enderecoEmail || '-',
        possuiAssinatura: usuarioPossuiAssinatura(u),
      }))
      .filter((u) => u.id),
    [usuarios]
  );

  // Só usuários com assinatura entram no select; o já vinculado continua visível mesmo sem assinatura.
  const opcoesAssinante = (idSelecionado) => usuariosOptions.filter(
    (u) => u.possuiAssinatura || u.id === String(idSelecionado || '')
  );

  // Carrega as empresas da API ao montar.
  useEffect(() => {
    let isMounted = true;
    const loadEmpresas = async () => {
      try {
        // AJUSTE BACKEND AQUI: endpoint de listagem de empresas usado na tela.
        const data = await getEmpresa();
        if (isMounted) {
          const normalized = asArray(data)
            .map(normalizeEmpresa)
            .filter((empresa) => empresa?.idEmpresa);
          setEmpresas(normalized);
        }
      } catch (error) {
        if (isMounted) setEmpresas([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadEmpresas();
    return () => { isMounted = false; };
  }, []);

  // Carrega os contatos disponíveis para os selects de contato da empresa.
  useEffect(() => {
    let isMounted = true;
    const loadContatos = async () => {
      try {
        const data = await getContato();
        if (isMounted) setContatos(Array.isArray(data) ? data.map(normalizeContato).filter(Boolean) : []);
      } catch (error) {
        if (isMounted) setContatos([]);
      }
    };
    loadContatos();
    return () => {
      isMounted = false;
    };
  }, []);

  // Carrega os usuários para os selects de assinatura do relatório.
  useEffect(() => {
    let isMounted = true;
    getUser()
      .then((data) => { if (isMounted) setUsuarios(asArray(data)); })
      .catch(() => { if (isMounted) setUsuarios([]); });
    return () => { isMounted = false; };
  }, []);

  /*useEffect(() => {
    let isMounted = true;
    const loadAllCidades = async () => {
      try {
        // AJUSTE BACKEND AQUI: carga global de cidades para resolver nome na listagem.
        const data = await getAllCidades();
        if (isMounted) {
          setCidadesTodas(Array.isArray(data) && data.length > 0 ? data : mockCidades);
        }
      } catch (error) {
        if (isMounted) {
          setCidadesTodas(mockCidades);
        }
      }
    };
    loadAllCidades();
    return () => {
      isMounted = false;
    };
  }, []);*/

  // Recarrega as cidades do formulário sempre que a UF selecionada muda.
  useEffect(() => {
    let isMounted = true;
    const loadCidades = async () => {
      try {
        // AJUSTE BACKEND AQUI: endpoint de cidades por UF para o formulario.
        const data = await getCidadesByUf(selectedUf);
        if (isMounted) {
          setCidades(Array.isArray(data) && data.length > 0 ? data : []);
        }
      } catch (error) {
        if (isMounted) {
          setCidades([]);
        }
      }
    };
    loadCidades();
    return () => {
      isMounted = false;
    };
  }, [selectedUf]);

  const filteredEmpresas = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return empresas;
    return empresas.filter((empresa) => {
      const razao = empresa.razaoSocial || '';
      const cnpj = empresa.numrCnpj || '';
      const cpf = empresa.numrCpf || '';
      const endereco = empresa.endereco || '';
      return (
        razao.toLowerCase().includes(normalized) ||
        cnpj.toLowerCase().includes(normalized) ||
        cpf.toLowerCase().includes(normalized) ||
        endereco.toLowerCase().includes(normalized)
      );
    });
  }, [empresas, query]);

  const stats = useMemo(() => {
    const total = empresas.length;
    const comCnpj = empresas.filter((empresa) => onlyDigits(empresa.numrCnpj).length === 14).length;
    const comCpf = empresas.filter((empresa) => onlyDigits(empresa.numrCpf).length === 11).length;
    return { total, comCnpj, comCpf };
  }, [empresas]);

  // Handler de onChange por campo (checkbox usa `checked`, demais usam `value`).
  const handleFieldChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handler de onChange que aplica uma máscara (CNPJ/CPF/CEP) antes de salvar.
  const handleMaskedChange = (field, formatter) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: formatter(event.target.value) }));
  };

  // Abre o modal em modo de criação (reseta formulário, aba e UF padrão MT).
  const openModal = () => {
    setFormErrors({});
    setContactFormErrors({});
    setActiveModalTab('empresa');
    setEditingEmpresaId(null);
    setSelectedUf('MT');
    //setContactFormData({ tipo: 'tecnico', nome: '', telefone: '' });
    setFormData({
      ...INITIAL_FORM_DATA,
      idCliente: canManage ? '' : sessionClientId || '',
    });
    setIsModalOpen(true);
  };

  // Abre o modal em modo de edição, preenchendo o formulário com máscaras aplicadas.
  const openEditModal = (empresa) => {
    setFormErrors({});
    setContactFormErrors({});
    setActiveModalTab('empresa');
    setEditingEmpresaId(empresa.idEmpresa);
    setSelectedUf((empresa.uf || 'MT').toUpperCase());
    //setContactFormData({ tipo: 'tecnico', nome: '', telefone: '' });
    setFormData({
      idCliente: empresa.idCliente || '',
      razaoSocial: empresa.razaoSocial || '',
      numrCnpj: formatCnpj(empresa.numrCnpj || ''),
      numrCpf: formatCpf(empresa.numrCpf || ''),
      endereco: empresa.endereco || '',
      codgCep: formatCep(String(empresa.codgCep || '')),
      idCidade: empresa.idCidade || '',
      idUsuarioTecnico: empresa.idUsuarioTecnico || '',
      idUsuarioFinanceiro: empresa.idUsuarioFinanceiro || '',
      idUsuarioAdministrativo: empresa.idUsuarioAdministrativo || '',
      idUsuarioAssinatura1: empresa.idUsuarioAssinatura1 || '',
      idUsuarioAssinatura2: empresa.idUsuarioAssinatura2 || '',
    });
    setIsModalOpen(true);
  };

  // Fecha o modal e limpa aba/edição/erros.
  const closeModal = () => {
    setIsModalOpen(false);
    setActiveModalTab('empresa');
    setEditingEmpresaId(null);
    setFormErrors({});
    setContactFormErrors({});
  };

  // Handler de onChange dos campos do formulário de contato (aplica máscara no telefone).
  const handleContactFieldChange = (field) => (event) => {
    const value = field === 'numrTelefone' ? formatPhone(event.target.value) : event.target.value;
    setContactFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Valida e cria um novo contato pela aba do modal, adicionando-o à lista de contatos disponíveis.
  const handleAddContato = async () => {
    const errors = {};
    const telefoneDigits = onlyDigits(contactFormData.numrTelefone);
    const email = String(contactFormData.enderecoEmail || '').trim();

    if (!contactFormData.nomeCompleto.trim()) {
      errors.nomeCompleto = 'Nome obrigatório.';
    }

    if (!telefoneDigits) {
      errors.numrTelefone = 'Telefone obrigatório.';
    } else if (telefoneDigits.length < 10 || telefoneDigits.length > 11) {
      errors.numrTelefone = 'Telefone inválido.';
    }

    if (!email) {
      errors.enderecoEmail = 'Email obrigatório.';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.enderecoEmail = 'Email inválido.';
    }

    if (Object.keys(errors).length > 0) {
      setContactFormErrors(errors);
      return;
    }

    try {
      const contexto = {
        idCliente: formData.idCliente || sessionClientId || null,
        idEmpresa: editingEmpresaId || null,
      };

      const { response, payload } = await createContato(
        {
          nomeCompleto: contactFormData.nomeCompleto.trim(),
          numrCpf: contactFormData.numrCpf, // CPF do contato
          numrTelefone: telefoneDigits,
          enderecoEmail: email,
        },
        contexto.idCliente // passar apenas o idCliente
      );

      const novoContato = normalizeContato(
        response || {
          id: `contato-local-${Date.now()}`,
          ...payload,
          nomeCompleto: contactFormData.nomeCompleto.trim(),
          numrCpf: onlyDigits(contactFormData.numrCpf),
          numrTelefone: telefoneDigits,
          enderecoEmail: email,
        }
      );

      setContatos((prev) => [novoContato, ...prev]);
      setContactFormErrors({});
      setContactFormData((prev) => ({ ...prev, nomeCompleto: '', numrCpf: '', numrTelefone: '', enderecoEmail: '' }));
      showSuccessToast('Contato cadastrado com sucesso.');
      setActiveModalTab('empresa');
    } catch (error) {
      setContactFormErrors({ geral: error?.message || 'Não foi possível salvar o contato.' });
    }
  };

  // Valida: razão social obrigatória, exatamente um documento (CNPJ ou CPF) válido e CEP; retorna erros + dígitos.
  const validateForm = () => {
    const errors = {};
    if (!formData.razaoSocial.trim()) errors.razaoSocial = 'Razão social obrigatória.';

    const cnpjDigits = onlyDigits(formData.numrCnpj);
    const cpfDigits = onlyDigits(formData.numrCpf);
    const cepDigits = onlyDigits(formData.codgCep);

    if (!cnpjDigits && !cpfDigits) {
      errors.numrCnpj = 'Informe CNPJ ou CPF.';
      errors.numrCpf = 'Informe CNPJ ou CPF.';
    }

    if (cnpjDigits && cpfDigits) {
      errors.numrCnpj = 'Informe apenas um CNPJ ou um CPF.';
      errors.numrCpf = 'Informe apenas um CNPJ ou um CPF.';
    }

    if (formData.numrCnpj.trim() && cnpjDigits.length !== 14) errors.numrCnpj = 'CNPJ inválido.';
    if (formData.numrCpf.trim() && cpfDigits.length !== 11) errors.numrCpf = 'CPF inválido.';
    if (cepDigits && cepDigits.length !== 8) errors.codgCep = 'CEP inválido.';
    if (formData.idUsuarioAssinatura1 && formData.idUsuarioAssinatura1 === formData.idUsuarioAssinatura2) {
      errors.idUsuarioAssinatura2 = 'Escolha um usuário diferente da assinatura 1.';
    }

    return { errors, cnpjDigits, cpfDigits };
  };

  // Submete o form: valida, resolve o cliente da sessão, checa documento duplicado, cria/atualiza na API e refaz a lista.
  const [submitting, handleSave] = useSubmitLock(async (event) => {
    event.preventDefault();
    const { errors, cnpjDigits, cpfDigits } = validateForm();

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const effectiveClientId = formData.idCliente || sessionClientId;
    if (!effectiveClientId) {
      setFormErrors({ geral: 'Cliente da sessão não encontrado.' });
      return;
    }

    const duplicate = empresas.some((empresa) => {
      if (editingEmpresaId && empresa.idEmpresa === editingEmpresaId) return false;
      const empresaCnpj = onlyDigits(empresa.numrCnpj);
      const empresaCpf = onlyDigits(empresa.numrCpf);
      return (cnpjDigits && empresaCnpj === cnpjDigits) || (cpfDigits && empresaCpf === cpfDigits);
    });

    if (duplicate) {
      setFormErrors({ numrCnpj: 'Documento já cadastrado.' });
      return;
    }

    try {
      if (editingEmpresaId) {
        const { response, payload } = await updateEmpresa(editingEmpresaId, formData, effectiveClientId);
        const updatedEmpresa = response
          ? normalizeEmpresa(response)
          : normalizeEmpresa({ ...payload, idEmpresa: editingEmpresaId });

        setEmpresas((prev) =>
          prev.map((empresa) => (empresa.idEmpresa === editingEmpresaId ? (updatedEmpresa || empresa) : empresa))
        );
        showSuccessToast('Empresa atualizada com sucesso.');
      } else {
        const { response: novaEmpresa, payload } = await createEmpresa(formData, effectiveClientId);
        const normalized = novaEmpresa
          ? normalizeEmpresa(novaEmpresa)
          : normalizeEmpresa({ ...payload, idEmpresa: `emp-${Date.now()}`, criadoEm: new Date().toISOString() });

        if (normalized) {
          setEmpresas((prev) => [normalized, ...prev]);
        }

        showSuccessToast('Empresa cadastrada com sucesso.');
      }

      closeModal();
    } catch (error) {
      setFormErrors({ geral: error.message || 'Não foi possível salvar a empresa.' });
    }
  });

  // Exclui a empresa após confirmação (restrito a admin) e remove da lista local.
  const handleDeleteEmpresa = async (empresaId) => {
    if (!canManage) return;

    const confirm = window.confirm("Deseja realmente excluir esta empresa?");
    if (!confirm) return;

    try {
      await deleteEmpresa(empresaId);
      setEmpresas((prev) => prev.filter((empresa) => empresa.idEmpresa !== empresaId));
      showSuccessToast('Empresa excluída com sucesso.');
    } catch (error) {
      showErrorToast(error.message || 'Não foi possível excluir a empresa.');
    }
  };

  return (
    <div className="empresa-page space-y-6">
      <section className="empresa-header flex flex-col gap-4 rounded-2xl bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">Gestão de empresas</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Visualize e gerencie as empresas cadastrados no sistema
          </p>
        </div>
        <button
          type="button"
          onClick={openModal}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          disabled={!canCreate}
        >
          <Plus className="h-4 w-4" />
          Nova empresa
        </button>
      </section>

      <section className="empresa-metrics grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Total</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.total}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Com CNPJ</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.comCnpj}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Com CPF</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.comCpf}</p>
        </div>
      </section>

      <section className="empresa-table overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3 sm:hidden">
          <ExpandableSearchFilter
            query={query}
            onChange={setQuery}
            placeholder="Buscar por razão social, CNPJ, CPF ou endereço"
            resultCount={filteredEmpresas.length}
          />
        </div>

        <div className="hidden overflow-x-auto sm:block">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-[0.3em] text-slate-400">
              <tr>
                <th className="px-4 py-3">Razão social</th>
                <th className="px-4 py-3">CNPJ</th>
                <th className="px-4 py-3">CPF</th>
                {/*<th className="px-4 py-3">Cliente</th>*/}
                <th className="px-4 py-3">Cidade</th>
                <th className="px-4 py-3">Contato técnico</th>
                <th className="px-4 py-3">Contato financeiro</th>
                <th className="px-4 py-3">Contato administrativo</th>
                <th className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <span>Ações</span>
                    <ExpandableSearchFilter
                      variant="inline"
                      query={query}
                      onChange={setQuery}
                      placeholder="Buscar..."
                      resultCount={filteredEmpresas.length}
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="border-b border-slate-100">
                    {Array.from({ length: 8 }).map((__, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-4">
                        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredEmpresas.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-6 text-center text-sm text-slate-500"
                  >
                    Nenhuma empresa encontrada.
                  </td>
                </tr>
              ) : (
                filteredEmpresas.map((empresa) => (
                  <tr key={empresa.idEmpresa} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/80">
                    <td className="px-4 py-4 font-medium text-slate-900">{empresa.razaoSocial}</td>
                    <td className="px-4 py-4 text-slate-600">{formatCnpj(empresa.numrCnpj || '') || '-'}</td>
                    <td className="px-4 py-4 text-slate-600">{formatCpf(empresa.numrCpf || '') || '-'}</td>
                    {/*<td className="px-4 py-4 text-slate-600">{empresa.idCliente || '-'}</td>*/}
                    <td className="px-4 py-4 text-slate-600">{empresa.nomeCidade || empresa.idCidade || '-'}</td>
                    <td className="px-4 py-4 text-slate-600">{findLabel(contatosDisponiveis, empresa.idUsuarioTecnico)}</td>
                    <td className="px-4 py-4 text-slate-600">{findLabel(contatosDisponiveis, empresa.idUsuarioFinanceiro)}</td>
                    <td className="px-4 py-4 text-slate-600">{findLabel(contatosDisponiveis, empresa.idUsuarioAdministrativo)}</td>
                    {/* AJUSTE BACKEND AQUI: estes campos leem a lista de contatos carregada da API. */}
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setViewEmpresa(empresa)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490]"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Visualizar
                        </button>
                        {canManage ? (
                          <>
                            <button
                              type="button"
                              onClick={() => openEditModal(empresa)}
                              className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteEmpresa(empresa.idEmpresa)}
                              className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Excluir
                            </button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 sm:hidden">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={`mobile-skeleton-${index}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
                <div className="mt-3 h-3 w-2/3 animate-pulse rounded bg-slate-100" />
                <div className="mt-3 h-3 w-1/3 animate-pulse rounded bg-slate-100" />
              </div>
            ))
          ) : filteredEmpresas.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              Nenhuma empresa encontrada.
            </div>
          ) : (
            filteredEmpresas.map((empresa) => (
              <div key={empresa.idEmpresa} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-base font-semibold text-slate-900">{empresa.razaoSocial}</p>
                    <p className="text-xs text-slate-500">
                      {formatCnpj(empresa.numrCnpj || '') || formatCpf(empresa.numrCpf || '') || 'Sem documento'}
                    </p>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                  <span>Cliente: {empresa.idCliente || '-'}</span>
                  <span>Cidade: {empresa.nomeCidade || empresa.idCidade || '-'}</span>
                  <span className="col-span-2">Endereço: {empresa.endereco || '-'}</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setViewEmpresa(empresa)}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490]"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Visualizar
                  </button>
                  {canManage ? (
                    <>
                      <button
                        type="button"
                        onClick={() => openEditModal(empresa)}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteEmpresa(empresa.idEmpresa)}
                        className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Excluir
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <RecordViewModal
        open={!!viewEmpresa}
        onClose={() => setViewEmpresa(null)}
        title="Detalhes da empresa"
        sections={viewEmpresa ? [
          {
            title: 'Dados gerais',
            fields: [
              { label: 'Razão social', value: viewEmpresa.razaoSocial || '-' },
              { label: 'CNPJ', value: formatCnpj(viewEmpresa.numrCnpj || '') || '-' },
              { label: 'CPF', value: formatCpf(viewEmpresa.numrCpf || '') || '-' },
            ],
          },
          {
            title: 'Endereço',
            fields: [
              { label: 'Endereço', value: viewEmpresa.endereco || '-' },
              { label: 'CEP', value: formatCep(String(viewEmpresa.codgCep || '')) || '-' },
              { label: 'Cidade', value: viewEmpresa.nomeCidade || viewEmpresa.idCidade || '-' },
              { label: 'UF', value: viewEmpresa.uf || '-' },
            ],
          },
          {
            title: 'Contatos',
            fields: [
              { label: 'Contato técnico', value: findLabel(contatosDisponiveis, viewEmpresa.idUsuarioTecnico) },
              { label: 'Contato financeiro', value: findLabel(contatosDisponiveis, viewEmpresa.idUsuarioFinanceiro) },
              { label: 'Contato administrativo', value: findLabel(contatosDisponiveis, viewEmpresa.idUsuarioAdministrativo) },
            ],
          },
          {
            title: 'Assinaturas do relatório',
            fields: [
              { label: 'Assinatura 1', value: viewEmpresa.idUsuarioAssinatura1 ? findLabel(usuariosOptions, viewEmpresa.idUsuarioAssinatura1) : '-' },
              { label: 'Assinatura 2', value: viewEmpresa.idUsuarioAssinatura2 ? findLabel(usuariosOptions, viewEmpresa.idUsuarioAssinatura2) : '-' },
            ],
          },
        ] : []}
      />

      {isModalOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4 py-4 sm:py-0">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            onClick={closeModal}
            aria-label="Fechar modal"
          />
          <form
            onSubmit={handleSave}
            className="relative z-10 w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-4 shadow-xl sm:p-6"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Cadastro</p>
                <h3 className="text-xl font-semibold text-slate-900">
                  {editingEmpresaId ? 'Editar empresa' : 'Cadastrar empresa'}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Preencha os dados da empresa.
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {formErrors.geral ? (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {formErrors.geral}
              </div>
            ) : null}

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveModalTab('empresa')}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${activeModalTab === 'empresa'
                  ? 'border-[#0e7490] bg-[#0e7490]/10 text-[#0e7490]'
                  : 'border-slate-200 text-slate-600 hover:border-blue-200 hover:text-blue-600'
                  }`}
              >
                Dados da empresa
              </button>
              <button
                type="button"
                onClick={() => setActiveModalTab('contato')}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${activeModalTab === 'contato'
                  ? 'border-[#0e7490] bg-[#0e7490]/10 text-[#0e7490]'
                  : 'border-slate-200 text-slate-600 hover:border-blue-200 hover:text-blue-600'
                  }`}
              >
                Cadastro de contato
              </button>
            </div>

            {activeModalTab === 'empresa' ? (
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <label htmlFor="empresa-razao" className="text-sm font-medium text-slate-700">
                    Razão social <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="empresa-razao"
                    type="text"
                    value={formData.razaoSocial}
                    onChange={handleFieldChange('razaoSocial')}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                  {formErrors.razaoSocial ? <p className="text-xs text-rose-600">{formErrors.razaoSocial}</p> : null}
                </div>

                <div className="space-y-2">
                  <label htmlFor="empresa-cnpj" className="text-sm font-medium text-slate-700">
                    CNPJ <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="empresa-cnpj"
                    type="text"
                    value={formData.numrCnpj}
                    onChange={handleMaskedChange('numrCnpj', formatCnpj)}
                    onKeyDown={(event) => {
                      if (isControlKey(event)) {
                        return;
                      }
                      if (event.key.length === 1 && !/[\d./-]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                  {formErrors.numrCnpj ? <p className="text-xs text-rose-600">{formErrors.numrCnpj}</p> : null}
                </div>

                <div className="space-y-2">
                  <label htmlFor="empresa-cpf" className="text-sm font-medium text-slate-700">
                    CPF <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="empresa-cpf"
                    type="text"
                    value={formData.numrCpf}
                    onChange={handleMaskedChange('numrCpf', formatCpf)}
                    onKeyDown={(event) => {
                      if (isControlKey(event)) {
                        return;
                      }
                      if (event.key.length === 1 && !/[\d.-]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                  {formErrors.numrCpf ? <p className="text-xs text-rose-600">{formErrors.numrCpf}</p> : null}
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <label htmlFor="empresa-endereco" className="text-sm font-medium text-slate-700">
                    Endereço:
                  </label>
                  <input
                    id="empresa-endereco"
                    type="text"
                    value={formData.endereco}
                    onChange={handleFieldChange('endereco')}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>

                <div className="sm:col-span-2 grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <label htmlFor="empresa-cep" className="text-sm font-medium text-slate-700">
                      CEP:
                    </label>
                    <input
                      id="empresa-cep"
                      type="text"
                      inputMode="numeric"
                      value={formData.codgCep}
                      onChange={handleMaskedChange('codgCep', formatCep)}
                      onKeyDown={(event) => {
                        if (isControlKey(event)) {
                          return;
                        }
                        if (event.key.length === 1 && !/\d/.test(event.key)) {
                          event.preventDefault();
                        }
                      }}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                    {formErrors.codgCep ? <p className="text-xs text-rose-600">{formErrors.codgCep}</p> : null}
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="empresa-uf" className="text-sm font-medium text-slate-700">
                      UF:
                    </label>
                    <select
                      id="empresa-uf"
                      value={selectedUf}
                      onChange={(event) => {
                        const uf = event.target.value;
                        setSelectedUf(uf);
                        setFormData((prev) => ({ ...prev, idCidade: '' }));
                      }}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      {UF_OPTIONS.map((uf) => (
                        <option key={uf} value={uf}>{uf}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="empresa-cidade" className="text-sm font-medium text-slate-700">
                      Cidade:
                    </label>
                    <SearchAutocompleteSelect
                      id="empresa-cidade"
                      value={formData.idCidade}
                      onChange={handleFieldChange('idCidade')}
                      placeholder="Buscar cidade"
                      options={cidades.map((cidade) => ({
                        id: cidade.idCidade || cidade.id,
                        label: cidade.nomeCidade || cidade.nome,
                      }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="empresa-tecnico" className="text-sm font-medium text-slate-700">
                    Contato técnico:
                  </label>
                  <select
                    id="empresa-tecnico"
                    value={formData.idUsuarioTecnico}
                    onChange={handleFieldChange('idUsuarioTecnico')}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="">Selecione</option>
                    {contatosDisponiveis.map((usuario) => (
                      <option key={usuario.id} value={usuario.id}>
                        {usuario.nome}
                      </option>
                    ))}
                  </select>
                  {/* AJUSTE BACKEND AQUI: select preenchido pela lista recebida da API. */}
                </div>

                <div className="space-y-2">
                  <label htmlFor="empresa-financeiro" className="text-sm font-medium text-slate-700">
                    Contato financeiro:
                  </label>
                  <select
                    id="empresa-financeiro"
                    value={formData.idUsuarioFinanceiro}
                    onChange={handleFieldChange('idUsuarioFinanceiro')}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="">Selecione</option>
                    {contatosDisponiveis.map((usuario) => (
                      <option key={usuario.id} value={usuario.id}>
                        {usuario.nome}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label htmlFor="empresa-administrativo" className="text-sm font-medium text-slate-700">
                    Contato administrativo:
                  </label>
                  <select
                    id="empresa-administrativo"
                    value={formData.idUsuarioAdministrativo}
                    onChange={handleFieldChange('idUsuarioAdministrativo')}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="">Selecione</option>
                    {contatosDisponiveis.map((usuario) => (
                      <option key={usuario.id} value={usuario.id}>
                        {usuario.nome}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label htmlFor="empresa-assinatura-1" className="text-sm font-medium text-slate-700">
                    Assinatura 1 do relatório:
                  </label>
                  <select
                    id="empresa-assinatura-1"
                    value={formData.idUsuarioAssinatura1}
                    onChange={handleFieldChange('idUsuarioAssinatura1')}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="">Nenhuma</option>
                    {opcoesAssinante(formData.idUsuarioAssinatura1).map((usuario) => (
                      <option key={usuario.id} value={usuario.id}>
                        {usuario.nome}{usuario.possuiAssinatura ? '' : ' (sem assinatura)'}
                      </option>
                    ))}
                  </select>
                  {formErrors.idUsuarioAssinatura1 ? (
                    <p className="text-xs text-rose-600">{formErrors.idUsuarioAssinatura1}</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <label htmlFor="empresa-assinatura-2" className="text-sm font-medium text-slate-700">
                    Assinatura 2 do relatório:
                  </label>
                  <select
                    id="empresa-assinatura-2"
                    value={formData.idUsuarioAssinatura2}
                    onChange={handleFieldChange('idUsuarioAssinatura2')}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="">Nenhuma</option>
                    {opcoesAssinante(formData.idUsuarioAssinatura2).map((usuario) => (
                      <option key={usuario.id} value={usuario.id}>
                        {usuario.nome}{usuario.possuiAssinatura ? '' : ' (sem assinatura)'}
                      </option>
                    ))}
                  </select>
                  {formErrors.idUsuarioAssinatura2 ? (
                    <p className="text-xs text-rose-600">{formErrors.idUsuarioAssinatura2}</p>
                  ) : null}
                </div>
                <p className="text-xs text-slate-400 sm:col-span-2">
                  Só aparecem usuários com assinatura/carimbo gravado no cadastro de usuário. As imagens saem no rodapé dos relatórios desta empresa.
                </p>
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Nome completo <span className="text-rose-500">*</span></label>
                    <input
                      type="text"
                      value={contactFormData.nomeCompleto}
                      onChange={handleContactFieldChange('nomeCompleto')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                    {contactFormErrors.nomeCompleto ? <p className="text-xs text-rose-600">{contactFormErrors.nomeCompleto}</p> : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">CPF:</label>
                    <input
                      type="text"
                      value={contactFormData.numrCpf}
                      onChange={(event) => setContactFormData((prev) => ({
                        ...prev,
                        numrCpf: formatCpf(event.target.value),
                      }))}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Telefone <span className="text-rose-500">*</span></label>
                    <input
                      type="text"
                      value={contactFormData.numrTelefone}
                      onChange={handleContactFieldChange('numrTelefone')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                    {contactFormErrors.numrTelefone ? <p className="text-xs text-rose-600">{contactFormErrors.numrTelefone}</p> : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Email <span className="text-rose-500">*</span></label>
                    <input
                      type="email"
                      value={contactFormData.enderecoEmail}
                      onChange={handleContactFieldChange('enderecoEmail')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                    {contactFormErrors.enderecoEmail ? <p className="text-xs text-rose-600">{contactFormErrors.enderecoEmail}</p> : null}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  O contato cadastrado fica disponível nos campos de contato técnico, financeiro e administrativo.
                </div>
                {contactFormErrors.geral ? (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm font-medium text-rose-600">
                    {contactFormErrors.geral}
                  </div>
                ) : null}
                <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => setActiveModalTab('empresa')}
                    className="w-full rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600 sm:w-auto"
                  >
                    Voltar para empresa
                  </button>
                  <button
                    type="button"
                    onClick={handleAddContato}
                    className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 sm:w-auto"
                  >
                    Salvar contato
                  </button>
                </div>
              </div>
            )}

            {activeModalTab === 'empresa' ? (
              <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600 w-full sm:w-auto"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 w-full sm:w-auto"
                >
                  {submitting ? 'Salvando...' : editingEmpresaId ? 'Salvar alterações' : 'Salvar empresa'}
                </button>
              </div>
            ) : null}
          </form>
        </div>
      ) : null}
    </div>
  );
}

export default EmpresaPage;



